import React, {useEffect, useMemo, useRef, useState} from 'react'
import PageLayout from '../../components/layout/PageLayout'
import useQueryReport from '../../hooks/api/report/useQueryReport'
import TableHeader from '../../components/table/TableHeader'
import Text from '../../components/text/Text'
import TableLayout from '../../components/table/TableLayout'
import {IReport} from '../../apis/report/getReport'
import TableItem from '../../components/table/TableItem'
import WizImage from '../../components/image/WizImage'
import {useImage} from '../../utils/image'
import {useTime} from '../../utils/time'
import WizButton from '../../components/button/WizButton'
import ChangedStateModal from '../../features/page/report/ChangedStateModal'
import WizTag from '../../components/tag/WizTag'
import useNavigation from '../../hooks/useNavigation'
import WizPagination from '../../components/pagination/WizPagination'

interface IProps {
    className?: string
}

const ReportPage: React.FC<IProps> = ({className}) => {
    const {renderThumbnail} = useImage()
    const {localTimeString} = useTime()
    const {goPostDetail, goCommentDetail, goUserDetail} = useNavigation()

    const [currentPage, setCurrentPage] = useState(1)

    const {data, refetch} = useQueryReport(currentPage)

    const changedStateModalRef = useRef<any>()

    useEffect(() => {
        const currentPageParams = new URLSearchParams(window.location.search).get('page')
        setCurrentPage(Number(currentPageParams) || 1)
    }, [])

    const reportList = useMemo(() => {
        const list: IReport[] = []
        data?.data.map(item => list.push(item))
        return list
    }, [data])

    useEffect(() => {
        refetch()
    }, [currentPage])

    const renderCheckState = state => {
        if (state) {
            return (
                <WizTag className={'sm:mx-auto sm:w-min'} state={true}>
                    Resolved
                </WizTag>
            )
        } else {
            return (
                <WizTag className={'sm:mx-auto sm:w-min'} state={false}>
                    Unresolved
                </WizTag>
            )
        }
    }

    const onClickChangedResolved = data => {
        changedStateModalRef?.current?.show(data)
    }

    const onClickPostLink = async (item: IReport) => {
        if (item?.parent_type === 'Post') {
            goPostDetail(item?.parent_id)
        } else {
            goCommentDetail(item?.parent_id)
        }
    }

    const isCheckNewReport = createdAt => {
        const deadline = 1000 * 60 * 60 * 24
        if (new Date().getTime() - new Date(createdAt * 1000).getTime() < deadline * 2) {
            return true
        }
    }

    return (
        <PageLayout title={'Report'} className={className}>
            <TableHeader
                className={
                    'grid-cols-[200px_1fr_185px_90px_180px] lg:grid-cols-[200px_1fr_155px_90px_180px] md:hidden gap-x-[10px]'
                }>
                <Text>Reporter</Text>
                <Text>Content</Text>
                <Text>Report Time</Text>
                <Text>State</Text>
                <Text>Action</Text>
            </TableHeader>

            <TableLayout>
                {reportList?.map(report => (
                    <TableItem
                        key={report?.id}
                        className={
                            'grid-cols-[200px_1fr_185px_90px_180px] lg:grid-cols-[200px_1fr_155px_90px_180px] gap-x-[10px] md:grid-cols-3 sm:block'
                        }
                        onClick={() => onClickPostLink(report)}>
                        <div
                            className={'flex items-center space-x-[10px] md:col-span-3 sm:justify-center'}
                            onClick={e => {
                                e.stopPropagation()
                                goUserDetail(report?.made_by?.id)
                            }}>
                            <div className={'flex-none'}>
                                <WizImage
                                    src={renderThumbnail(report?.made_by?.thumbnail)}
                                    className={'w-[32px] h-[32px] rounded-full'}
                                />
                            </div>
                            <Text className={'truncate md:text-ti2 md:text-font02'}>
                                {report?.made_by?.name || '-'}
                            </Text>
                        </div>
                        <div
                            className={
                                'flex items-center space-x-[5px] md:col-span-3 sm:text-center sm:py-[20px] sm:block'
                            }>
                            <Text
                                className={`text-ti2 w-[95%] md:text-ti1 md:w-full ${
                                    isCheckNewReport(report?.CreatedAt) ? '' : 'md:px-[40px]'
                                }`}>
                                [{report?.parent_type}]
                                 {' ' + (report?.extra || '-')}
                            </Text>
                            {isCheckNewReport(report?.CreatedAt) && (
                                <div
                                    className={
                                        'bg-line_skyblue text-sub_blue text-ti4 px-[5px] py-[2px] rounded-[5px] sm:mt-[5px]'
                                    }>
                                    NEW
                                </div>
                            )}
                        </div>
                        <div className={'text-font03 text-body2 lg:text-body3 md:text-center flex md:flex-col justify-center'}>
                            <Text className={'hidden md:block md:pt-[10px] md:pb-[5px] md:text-ti3 md:text-font03'}>
                                Report At
                            </Text>
                            {localTimeString(report?.CreatedAt)}
                        </div>
                        <div className={'md:text-center md:mx-auto'}>
                            <Text className={'hidden md:block md:pt-[10px] md:pb-[5px] md:text-ti3 md:text-font03'}>
                                Resolved
                            </Text>
                            {renderCheckState(report?.Checked)}
                        </div>
                        <div className={'md:text-center'}>
                            <Text className={'hidden md:block md:pt-[10px] md:pb-[5px] md:text-ti3 md:text-font03'}>
                                Action
                            </Text>
                            <WizButton
                                size={'small'}
                                type={'blue'}
                                text={'Changed Resolved'}
                                onClick={e => {
                                    onClickChangedResolved(report)
                                    e?.preventDefault()
                                    e?.stopPropagation()
                                }}
                            />
                        </div>
                    </TableItem>
                ))}

                {!reportList?.length && (
                    <TableItem className={'text-center'}>
                        <div>
                            <Text>No Items</Text>
                        </div>
                    </TableItem>
                )}
            </TableLayout>

            <ChangedStateModal ref={changedStateModalRef} currentPage={currentPage} />

            <WizPagination
                totalPage={data?.pageInfo?.total_count}
                currentPage={currentPage}
                onPageChange={item => {
                    setCurrentPage(item)
                    window.history.pushState('', '', `?page=${item}`)
                }}
            />
        </PageLayout>
    )
}

export default ReportPage
